import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSavedSearches, saveSearch, deleteSavedSearch } from '../services/savedSearchesService';
import { FilterState, ViewMode, SavedSearch } from '../types';
import { toLocationSlug } from '../lib/slugs';
import { useRouteStateSlug } from './useRouteStateSlug';
import { DEFAULT_STATE } from '../lib/stateCore.mjs';

export function useDirectorySavedSearches(filters: FilterState, viewMode: ViewMode) {
    const { user } = useAuth();
    // UI-01: the state the hosting directory page is scoped to — stamped onto new
    // saves so applying one later rebuilds the URL for the state it was saved
    // from, and used to keep the "already saved" matcher from cross-state false
    // matches (postcode-less slugs collide on 19 suburb names across NSW/VIC).
    const routeStateSlug = useRouteStateSlug();
    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
    const [isSavingSearch, setIsSavingSearch] = useState(false);
    const [isSaveSearchModalOpen, setIsSaveSearchModalOpen] = useState(false);
    const [saveSearchName, setSaveSearchName] = useState('');
    const [isSavedSearchesDropdownOpen, setIsSavedSearchesDropdownOpen] = useState(false);

    // Ref for click-outside detection
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load saved searches ONCE per user (was a realtime onSnapshot listener).
    // Saved searches change only via this user's own save/delete actions, which
    // update local state optimistically below — so a live listener and its
    // continuous reads aren't needed (Phase 1 item 15). Trade-off: no automatic
    // cross-tab live sync of saved searches.
    useEffect(() => {
        if (!user) {
            setSavedSearches([]);
            return;
        }
        let active = true;
        getSavedSearches(user.uid)
            .then(list => { if (active) setSavedSearches(list); })
            .catch(() => { if (active) setSavedSearches([]); });
        return () => { active = false; };
    }, [user]);

    // Check if current search is saved
    const currentSearchMatchesSaved = useMemo(() => {
        if (!filters.suburb) return null;
        return savedSearches.find(s =>
            // UI-01: legacy saves (state null/absent) were always applied against
            // DEFAULT_STATE, so that is the state they match as.
            (s.state ?? DEFAULT_STATE) === routeStateSlug &&
            toLocationSlug(s.location) === toLocationSlug(filters.suburb) &&
            s.radius === filters.radius &&
            s.useRadius === filters.useRadius &&
            JSON.stringify([...s.filters.styles].sort()) === JSON.stringify([...filters.styles].sort()) &&
            JSON.stringify([...s.filters.ageGroups].sort()) === JSON.stringify([...filters.ageGroups].sort()) &&
            JSON.stringify([...(s.filters.experienceLevels || [])].sort()) === JSON.stringify([...(filters.experienceLevels || [])].sort())
        ) || null;
    }, [filters, savedSearches, routeStateSlug]);

    const handleSaveSearch = async () => {
        if (!user || !filters.suburb) return;

        setIsSavingSearch(true);
        try {
            const created = await saveSearch(
                user.uid,
                saveSearchName || `${filters.suburb} search`,
                filters.suburb,
                filters.radius,
                filters.useRadius,
                {
                    styles: filters.styles,
                    ageGroups: filters.ageGroups,
                    experienceLevels: filters.experienceLevels
                },
                viewMode,
                // UI-01: stamp the saving page's state onto the document.
                routeStateSlug,
                false
            );
            // Optimistic local update (replaces the realtime listener); newest
            // first to match getSavedSearches' createdAt-desc order.
            setSavedSearches(prev => [created, ...prev]);
            setIsSaveSearchModalOpen(false);
            setSaveSearchName('');
        } catch (error) {
            console.error('Error saving search:', error);
        } finally {
            setIsSavingSearch(false);
        }
    };

    const handleUnsaveSearch = async () => {
        if (!user || !currentSearchMatchesSaved) return;

        try {
            await deleteSavedSearch(user.uid, currentSearchMatchesSaved.id);
            setSavedSearches(prev => prev.filter(s => s.id !== currentSearchMatchesSaved.id));
        } catch (error) {
            console.error('Error deleting saved search:', error);
        }
    };

    return {
        savedSearches,
        isSavingSearch,
        isSaveSearchModalOpen,
        setIsSaveSearchModalOpen,
        saveSearchName,
        setSaveSearchName,
        isSavedSearchesDropdownOpen,
        setIsSavedSearchesDropdownOpen,
        currentSearchMatchesSaved,
        handleSaveSearch,
        handleUnsaveSearch,
        dropdownRef
    };
}
